import React, { useRef, useLayoutEffect, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import "../styles/Journey.css";

const journeySections = {
  "Architectural Foundations":
    "Transitioned from a background in commerce to mastering Computer Science through high speed iteration. What started as a fascination with problem solving evolved into a career defined by building systems that do not just work. They scale.",

  "The Deloitte Proving Ground":
    "At Deloitte, I evolved from a frontend specialist into a disciplined full stack engineer. Navigating high pressure environments, I learned to own the entire lifecycle of a project, turning fragmented data and early stage GenAI concepts into production ready solutions for the region’s most demanding clients.",

  "Hyper-growth & AI Leadership":
    "Currently serving as the Head of AI for the world’s fastest growing AI Web3 project. In 11 months, I scaled from individual contributor to departmental lead, overseeing the convergence of decentralized tech and machine learning while maintaining a relentless focus on runtime efficiency and user experience."
};

const journeySteps = [
  {
    title: "End to End Ownership",
    text: "I build with the full stack in mind. Whether it’s a prototype or a long term core product, I ensure the architecture is clean, the logic is sound, and the execution is seamless from the database to the UI.",
  },
  {
    title: "Edge Case Mastery",
    text: "I have an instinctive ability to identify bottlenecks and edge cases before they hit production. My focus is on preemptive stability, solving the 'bug' before it exists.",
  },
  {
    title: "Efficiency First Mindset",
    text: "Technical debt isn't an option. I prioritize optimized runtime complexity and a frictionless user experience, ensuring that high performance AI doesn't come at the cost of usability.",
  },
];


const Journey = () => {
  const sectionRef = useRef(null);
  const railRef = useRef(null);
  const groupRefs = useRef([]);
  const [circlePositions, setCirclePositions] = useState([]);

  const entries = Object.entries(journeySections);

  // Scroll progress for this section only (0 → 1)
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start 100%", "end 40%"],
  });
  

  // Line fill amount (0 = no white, 1 = fully white)
  const lineScaleY = useTransform(scrollYProgress, [0, 1], [0, 1]);

  // Circle styling reacts to scroll (all circles share same animation for now)
  const circleBorderColor = useTransform(
    scrollYProgress,
    [0, 1],
    ["rgba(148,163,184,0.5)", "rgba(249,250,251,1)"]
  );

  const circleInnerColor = useTransform(
    scrollYProgress,
    [0, 1],
    ["rgba(15,23,42,1)", "rgba(30,64,175,1)"]
  );

  // Measure where each title sits vertically relative to the rail
  const measureCirclePositions = () => {
    if (!railRef.current) return;

    const railRect = railRef.current.getBoundingClientRect();
    const positions = groupRefs.current.map((groupEl) => {
      if (!groupEl) return 0.5;

      const titleEl = groupEl.querySelector(".journey-group-title") || groupEl;
      const titleRect = titleEl.getBoundingClientRect();

      // Take the vertical center of the title
      const titleCenter = titleRect.top + titleRect.height / 2;

      const offset = titleCenter - railRect.top;
      const t = railRect.height > 0 ? offset / railRect.height : 0.5;

      // Clamp a bit so we don't go outside rail
      const clamped = Math.min(0.97, Math.max(0.03, t));
      return clamped;
    });

    setCirclePositions(positions);
  };

  useLayoutEffect(() => {
    measureCirclePositions();
  }, []);

  // Re-measure on resize because layout shifts
  useEffect(() => {
    const handleResize = () => measureCirclePositions();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section ref={sectionRef} className="journey-section" id="journey">
      <div className="journey-inner">
        <div className="journey-layout">
          {/* Timeline column: vertical line + one circle per journey section */}
          <div className="journey-rail">
            <div className="journey-rail-inner" ref={railRef}>
              {/* Line */}
              <div className="journey-line">
                <div className="journey-line-track" />
                <motion.div
                  className="journey-line-progress"
                  style={{ scaleY: lineScaleY }}
                />
              </div>

              {/* Circles – one per journey title */}
              {entries.map(([title], index) => {
                const t = circlePositions[index] ?? 0.5;
                const top = `${t * 100}%`;

                return (
                  <motion.div
                    key={title}
                    className="journey-circle"
                    style={{
                      top,
                      borderColor: circleBorderColor,
                      backgroundColor: circleInnerColor,
                    }}
                  >
                    <div className="journey-circle-dot" />
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Content column */}
          <div className="journey-content">
            <p className="journey-eyebrow">Journey</p>

            <div className="journey-groups">
              {entries.map(([title, subtitle], index) => (
                <motion.section
                  key={title}
                  className="journey-group"
                  ref={(el) => (groupRefs.current[index] = el)}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                >
                  <header className="journey-group-header">
                    <h2 className="journey-group-title">{title}</h2>
                    <p className="journey-group-subtitle">{subtitle}</p>
                  </header>

                  {/* Only the FIRST journey section gets the row of sub blocks */}
                  {index === 0 && (
                    <div className="journey-steps">
                      {journeySteps.map((step) => (
                        <motion.article
                          key={step.title}
                          className="journey-step"
                          initial={{ opacity: 0, y: 16 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true, amount: 0.4 }}
                          transition={{ duration: 0.35 }}
                        >
                          <h3 className="journey-step-title">
                            {step.title}
                          </h3>
                          <p className="journey-step-text">{step.text}</p>
                        </motion.article>
                      ))}
                    </div>
                  )}
                </motion.section>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Journey;
